/**
 * 企业大脑 — 权限状态管理 (Zustand)
 */
import { create } from 'zustand';
import * as permissionApi from '@/services/permission';
import type { MemberPermissions } from '@/services/permission';

export const MODULE_ROUTES: Record<string, string[]> = {
  tools: ['/tools'],
  apps: ['/apps'],
  wins: ['/wins'],
  knowledge: ['/knowledge'],
  connectors: ['/connectors', '/monitor-tasks', '/webhooks'],
  org: ['/org'],
  tenants: ['/tenants'],
  _public: ['/skillhub', '/call-management', '/sessions', '/docs', '/deploy', '/remote'],
};

export const ROUTE_TO_MODULE: Record<string, string> = {
  '/tools': 'tools',
  '/apps': 'apps',
  '/wins': 'wins',
  '/knowledge': 'knowledge',
  '/connectors': 'connectors',
  '/monitor-tasks': 'connectors',
  '/webhooks': 'connectors',
  '/org': 'org',
  '/tenants': 'tenants',
  '/skillhub': '_public',
  '/call-management': '_public',
  '/sessions': '_public',
  '/docs': '_public',
  '/deploy': '_public',
  '/remote': '_public',
};

interface PermissionState {
  permissions: MemberPermissions | null;
  loading: boolean;
  error: string | null;
  fetchPermissions: () => Promise<void>;
  clearPermissions: () => void;
  hasModuleAccess: (moduleId: string) => boolean;
  hasConnectorAccess: (connectorId: string) => boolean;
  hasRouteAccess: (path: string) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
}

const getStoredPermissions = (): MemberPermissions | null => {
  try {
    const stored = localStorage.getItem('eb-permissions');
    if (!stored) return null;
    const data = JSON.parse(stored);
    if (data.timestamp && Date.now() - data.timestamp < 30 * 60 * 1000) {
      return data.permissions;
    }
    return null;
  } catch {
    return null;
  }
};

const storePermissions = (permissions: MemberPermissions) => {
  localStorage.setItem('eb-permissions', JSON.stringify({
    permissions,
    timestamp: Date.now(),
  }));
};

export const usePermissionStore = create<PermissionState>((set, get) => ({
  permissions: getStoredPermissions(),
  loading: false,
  error: null,
  
  fetchPermissions: async () => {
    const cached = get().permissions;
    if (cached && cached.member_id) {
      return;
    }
    
    set({ loading: true, error: null });
    try {
      const permissions = await permissionApi.getMyPermissions();
      storePermissions(permissions);
      set({ permissions, loading: false });
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      set({ 
        error: '获取权限信息失败，请刷新页面重试', 
        loading: false,
        permissions: null,
      });
    }
  },
  
  clearPermissions: () => {
    localStorage.removeItem('eb-permissions');
    set({ permissions: null, loading: false, error: null });
  },
  
  hasModuleAccess: (moduleId: string) => {
    const { permissions, loading } = get();
    if (!permissions || loading) return false;
    if (permissions.is_admin) return true;
    return permissions.effective_modules?.includes(moduleId) ?? false;
  },
  
  hasConnectorAccess: (connectorId: string) => {
    const { permissions, loading } = get();
    if (!permissions || loading) return false;
    if (permissions.is_admin) return true;
    return permissions.effective_connector_subs?.includes(connectorId) ?? false;
  },
  
  hasRouteAccess: (path: string) => {
    const moduleId = ROUTE_TO_MODULE[path];
    if (!moduleId) return false;
    
    const { permissions, loading } = get();
    if (!permissions || loading) return false;
    
    if (moduleId === '_public') return true;
    
    if (moduleId === 'tenants') {
      return permissions.is_super_admin === true;
    }
    
    if (moduleId === 'org') {
      return permissions.is_admin;
    }
    
    return get().hasModuleAccess(moduleId);
  },
  
  isAdmin: () => {
    const { permissions } = get();
    return permissions?.is_admin ?? false;
  },
  
  isSuperAdmin: () => {
    const { permissions } = get();
    return permissions?.is_super_admin ?? false;
  },
}));

export function useModuleAccess(moduleId: string): boolean {
  const hasAccess = usePermissionStore((state) => state.hasModuleAccess(moduleId));
  return hasAccess;
}

export function useConnectorAccess(connectorId: string): boolean {
  const hasAccess = usePermissionStore((state) => state.hasConnectorAccess(connectorId));
  return hasAccess;
}

export function useIsAdmin(): boolean {
  const isAdmin = usePermissionStore((state) => state.isAdmin());
  return isAdmin;
}

export function useIsSuperAdmin(): boolean {
  const isSuperAdmin = usePermissionStore((state) => state.isSuperAdmin());
  return isSuperAdmin;
}
